
import { Component, Prop, Vue } from "vue-property-decorator";
import WorldCupComponent from "@/libs/WorldCupComponent";
import { mapState } from "vuex";
import Deferrable from "@/libs/common/delegate/Deferrable";
import { Duct } from "@/libs/Duct";
import Defer from "@/libs/Defer";
import MatchInterface from "@/libs/model/MatchInterface";
import MatchInfo from "@/components/Match/MatchInfo.vue";
import groupBy from "lodash/groupBy";
import upperFirst from  "lodash/upperFirst";
import lob from "lodash/object";

@Component({
  components: {
    MatchInfo,
  },
  computed: {
    ...mapState({
      worldCupMatches: "worldCupMatches"
    })
  }
})
export default class Match
  extends WorldCupComponent
  implements Deferrable {
  config: any;
  worldCupMatches: any;
  isPreloaded: boolean;
  isMounted: boolean;
  defer: Defer;

  get matches(): Array<any>  {
    let list = this.getDataList();
    this.onDebug(this, "MostRelevant:getDataList", list);
    let groupList = this.getGroupMatches(list);
    this.onDebug(this, "MostRelevant:getGroupMatches", groupList);
    let relevant = this.getRelevant(groupList);
    this.onDebug(this, "MostRelevant:getRelevant", relevant);
    return relevant;
  }

  get isEmpty(): boolean {
    let list = this.getDataList();
    if(list && list.length > 0){
      return false;
    }
    return true;
  }

  getDataList(): Array<MatchInterface> {
    let data = this.getStoreData(this.worldCupMatches);

    if (!data) {
      return [];
    }

    if (!data.match) {
      return [];
    }

    let list = [...data.match];
    list = this.getSortedList(list);

    let show = this.getOption().mostRelevant.show;
    if(show === "all"){
      return list;
    }

    if(show.includes(",")){
      return this.getMixed(list, show);
    } else {
      return this.getFilteredList(list, show);
    }
  }

  get defaultMessageText(): string {
    return this.getOption().mostRelevant.defaultMessage.text;
  }

  get isDefaultMessageHtml(): boolean {
    return this.getOption().mostRelevant.defaultMessage.html;
  }

  get blinkInterval(): string {
    return this.getOption().mostRelevant.blinkInterval;
  }

  get matchIds(): any {
    return this.getOption().mostRelevant.matchIds;
  }

  get defaultMatchIds(): Array<string> {
    return this.getOption().mostRelevant.defaultMatchIds;
  }

  /**
   * This is a working sort function. No need to perform sort after the group, once
   * everything is sorted then expect that time is also sorted.
   * ~lem
   * @param list
   */
  getSortedList(list: Array<MatchInterface>){

    let sortOrder = this.getOption().mostRelevant.sortOrder;

    return list.sort((a,b)=>{
      let aDateTime = new Date(`${a.matchInfo.localDate}T${a.matchInfo.localTime}`);
      let bDateTime =new Date(`${b.matchInfo.localDate}T${b.matchInfo.localTime}`);
      if(sortOrder === "asc"){
        return aDateTime.getTime() - bDateTime.getTime();
      }
      if(sortOrder === "dsc"){
        return bDateTime.getTime() - aDateTime.getTime();
      }
      return 0;
    });
  }

  getGroupMatches( data:Array<MatchInterface>): any {
    const matchesGroupedByStage = groupBy(data, item => item.matchInfo.stage.name);
    const groupedDataByDate = {};
    for(const k in matchesGroupedByStage) {
      const matchesGroupedByDate = groupBy(matchesGroupedByStage[k], val => val.matchInfo.localDate);
      groupedDataByDate[k] = matchesGroupedByDate;
    }
    return groupedDataByDate;
  }

  getFlattenedData(data: any): Array<any> {
    let list = [];

    let stageNames = Object.keys(data);
    if(stageNames.length <= 0){
      return list;
    }

    stageNames.forEach((stageName) => {
      let stage = data[stageName];
      if(!stage){
        return;
      }

      let dates = Object.keys(stage);
      if(!dates || dates.length < 0){
        return;
      }

      dates.forEach((date) => {
        let batches = stage[date];
        if(!batches || batches.length < 0){
          return;
        }

        batches.forEach((batch) => {
          list.push({
            date: date,
            data: batch
          });
        });

      });

    });

    return list;
  }

  getRelevant(data: any): any {
    let list = this.getFlattenedData(data);
    let relevant = {};

    let fromCutOffDates = this.getFromMatchIdsByCutOffDate(list);
    if(fromCutOffDates && fromCutOffDates.length > 0){
      this.onDebug(this, "MostRelevant::fromCutOffDate", fromCutOffDates);
      fromCutOffDates.every((item)=>{
        let batch = relevant[item.date] ?? [];
        batch.push(item.data);
        relevant[item.date] = batch;
        return true;
      });
      return {"Relevant": relevant};
    }

    let fromMatchIds = this.getFromMatchIds(list);
    if(fromMatchIds && fromMatchIds.length > 0){
      this.onDebug(this, "MostRelevant::fromMatchIds", fromMatchIds);
      fromMatchIds.every((item)=>{
        let batch = relevant[item.date] ?? [];
        batch.push(item.data);
        relevant[item.date] = batch;
        return true;
      });
      return {"Relevant": relevant};
    }

    let fromDefaults = this.getFromDefaults(list);
    if(fromDefaults && fromDefaults.length > 0){
      this.onDebug(this, "MostRelevant::fromDefaults", fromDefaults);
      fromDefaults.every((item)=>{
        let batch = relevant[item.date] ?? [];
        batch.push(item.data);
        relevant[item.date] = batch;
        return true;
      });
      return {"Relevant": relevant};
    }

    let fromLatestDates = this.getFromLatestDates(list);
    if(fromLatestDates && fromLatestDates.length > 0){
      this.onDebug(this, "MostRelevant::fromLatestDates", fromLatestDates);
      fromLatestDates.every((item)=>{
        let batch = relevant[item.date] ?? [];
        batch.push(item.data);
        relevant[item.date] = batch;
        return true;
      });
      return {"Relevant": relevant};
    }

    this.onDebug(this, "MostRelevant::getRelevant", []);
    return {"Relevant": relevant};
  }

  getFromMatchIdsByCutOffDate(list: Array<any>): Array<any>{
    let cutOffDateString = this.getCutOffDateString();
    let total = 0;
    let batch = [];

    list.every((item) => {
      if (this.matchIds) {
        let matchId = lob.get(item, "data.matchInfo.id");
        if(!matchId){
          return true;
        }

        let matches = this.matchIds[cutOffDateString];
        if(matches && Array.isArray(matches) && matches.includes(matchId)){
          batch.push(item);
          total++;
          return true;
        }
      }
      return true;
    });

    return batch;
  }

  getFromMatchIds(list: Array<any>): Array<any>{
    let currentDate = new Date();
    let currentDateString = this.formatDate(currentDate);
    let total = 0;
    let batch = [];

    list.every((item) => {
      if(total >= 4) {
        return false;
      }
      let itemDate = new Date(item.date);
      if(this.matchIds) {
        let matchId = lob.get(item, "data.matchInfo.id");
        if(!matchId){
          return true;
        }

        if(Array.isArray(this.matchIds) && this.matchIds.includes(matchId)){
          if(this.isLatestDate(itemDate, currentDate)){
            batch.push(item);
            total++;
          }
          return true;
        }

        let matches = this.matchIds[currentDateString];
        if(matches && Array.isArray(matches) && matches.includes(matchId)){
          batch.push(item);
          total++;
          return true;
        }
        return true;
      }
      return true;
    });

    return batch;
  }

  getFromDefaults(list: Array<any>): Array<any> {
    let total = 0;
    let batch = [];

    list.every((item) => {
      if(total >= 4) {
        return false;
      }

      if(this.defaultMatchIds && this.defaultMatchIds.length > 0 ) {
        let matchId = lob.get(item, "data.matchInfo.id");
        if(!matchId){
          return true;
        }

        if(this.matchIds.includes(matchId)){
          batch.push(item);
          total++;
          return true;
        }
        return true;
      }
      return true;
    });

    return batch;
  }

  getFromLatestDates(list: Array<any>): Array<any> {
    let currentDate = new Date();
    let total = 0;
    let batch = [];

    list.every((item) => {
      if (total >= 4) {
        return false;
      }
      let itemDate = new Date(item.date);
      if(this.isLatestDate(itemDate,currentDate)){
        batch.push(item);
        total++;
        return true;
      }
      return true;
    });

    return batch;
  }

  getCutOffDateString(){
    let currentDate = new Date();
    let dateString = this.formatDate(currentDate);
    if(currentDate.getHours() >= 12 ) {
      return dateString;
    }

    currentDate.setDate(currentDate.getDate() - 1);
    dateString = this.formatDate(currentDate);
    return dateString;
  }

  formatDate(date: Date): string {
    let d = date.getDate().toString().length <= 1 ? "0" + date.getDate().toString() : date.getDate().toString();
    let mm = date.getMonth() + 1;
    let m = mm.toString().length <= 1 ? "0" + mm.toString() : mm.toString();
    let y = date.getFullYear();

    let dd =  `${m}-${d}-${y}`;
    // Put your debug console here
    return dd;
  }

  isLatestDate(subjectDate: Date, currentDate: Date): boolean {
    if(subjectDate.getFullYear() >= currentDate.getFullYear()
      && subjectDate.getMonth() >= currentDate.getMonth()
      && subjectDate.getDate() >= currentDate.getDate()){
      return true;
    }
    return false;
  }

  getFilteredList(list: Array<MatchInterface>, status: string){
    let statusName = upperFirst(status);
    return list.filter((match) => {
      if(match.liveData && match.liveData.matchDetails && match.liveData.matchDetails.matchStatus === statusName){
        return match;
      }
    });
  }

  getMixed(list: Array<MatchInterface>, status: string){
    let statuses = status.split(",").map((s)=>{
      return upperFirst(s.trim());
    });
    return list.filter((match) => {
      if(match.liveData && match.liveData.matchDetails){
        let matchStatus = match.liveData.matchDetails.matchStatus;
        if(statuses.includes(matchStatus)){
          return match;
        }
      }
    });
  }

  created() {
    Duct.open(Duct.Vanes.OnPreloadDone, this.onPreloadDone);
    this.defer = Defer.create().setDeferrable(this);
    this.defer.wait();
    this.onDebug(this, "MostRelevant:created", {});
  }

  mounted() {
    this.isMounted = true;
    this.onDebug(this, "MostRelevant:mounted", {});
  }

  onDefer() {
    if (this.isPreloaded && this.isMounted) {
      return false;
    }
    return true;
  }

  onReady() {
  }

  onPreloadDone() {
    this.isPreloaded = true;
  }

}
